import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface PriceRule {
  id: string;
  name: string;
  type: "discount" | "markup" | "fixed";
  value: number;
  appliesTo: "all" | "category" | "product";
  targetId?: string;
  targetName?: string;
  startDate?: string;
  endDate?: string;
  isActive: boolean;
}

interface PriceList {
  id: string;
  name: string;
  description: string;
  currency: string;
  isDefault: boolean;
  productCount: number;
}

const MOCK_PRICE_LISTS: PriceList[] = [
  { id: "1", name: "Retail", description: "Standard retail prices", currency: "USD", isDefault: true, productCount: 245 },
  { id: "2", name: "Wholesale", description: "Wholesale prices for bulk orders", currency: "USD", isDefault: false, productCount: 180 },
  { id: "3", name: "VIP", description: "Special prices for VIP customers", currency: "USD", isDefault: false, productCount: 120 },
];

const MOCK_PRICE_RULES: PriceRule[] = [
  { id: "1", name: "Summer Sale", type: "discount", value: 20, appliesTo: "all", startDate: "2025-06-01", endDate: "2025-08-31", isActive: true },
  { id: "2", name: "Electronics Markup", type: "markup", value: 15, appliesTo: "category", targetId: "1", targetName: "Electronics", isActive: true },
  { id: "3", name: "Clearance", type: "discount", value: 50, appliesTo: "product", targetId: "3", targetName: "USB-C Cable", isActive: false },
];

export default function PricingSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState<"lists" | "rules">("lists");
  const [showCreateModal, setShowCreateModal] = useState(false);

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    tableRowHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const getRuleTypeBadge = (type: PriceRule["type"]) => {
    const styles = {
      discount: "bg-emerald-500/20 text-emerald-500",
      markup: "bg-amber-500/20 text-amber-500",
      fixed: "bg-blue-500/20 text-blue-500",
    };
    return styles[type];
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>{t("posProducts.pricing.title")}</h1>
          <p className={themeClasses.textSecondary}>{t("posProducts.pricing.subtitle")}</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium flex items-center gap-2"
        >
          <i className="ri-add-line"></i>
          {activeTab === "lists" ? t("posProducts.pricing.addPriceList") : t("posProducts.pricing.addRule")}
        </button>
      </div>

      <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        <div className={`border-b ${themeClasses.border} p-1 flex gap-1`}>
          <button
            onClick={() => setActiveTab("lists")}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
              activeTab === "lists" ? "bg-primary-500 text-gray-900" : `${themeClasses.text} hover:bg-dark-card`
            }`}
          >
            <i className="ri-list-check-2"></i>
            {t("posProducts.pricing.priceLists")}
          </button>
          <button
            onClick={() => setActiveTab("rules")}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
              activeTab === "rules" ? "bg-primary-500 text-gray-900" : `${themeClasses.text} hover:bg-dark-card`
            }`}
          >
            <i className="ri-percent-line"></i>
            {t("posProducts.pricing.priceRules")}
          </button>
        </div>

        {activeTab === "lists" ? (
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {MOCK_PRICE_LISTS.map((list) => (
              <div key={list.id} className={`p-4 rounded-xl border ${themeClasses.border} ${themeClasses.cardBg}`}>
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-2">
                    <h3 className={`font-semibold ${themeClasses.text}`}>{list.name}</h3>
                    {list.isDefault && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-primary-500 text-gray-900">
                        {t("posProducts.pricing.default")}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button aria-label={t("general.edit", { ns: "common" })} className={`p-1.5 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                      <i className="ri-edit-line"></i>
                    </button>
                    <button aria-label={t("general.delete", { ns: "common" })} className="p-1.5 rounded-lg hover:bg-red-500/10 text-red-500">
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
                <p className={`text-sm ${themeClasses.textSecondary} mb-3`}>{list.description}</p>
                <div className="flex items-center justify-between">
                  <span className={`text-sm ${themeClasses.textSecondary}`}>
                    {list.productCount} {t("posProducts.pricing.products")}
                  </span>
                  <span className={`font-medium ${themeClasses.text}`}>{list.currency}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`${themeClasses.cardBg} border-b ${themeClasses.border}`}>
                <tr>
                  <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>
                    {t("posProducts.pricing.ruleName")}
                  </th>
                  <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>
                    {t("posProducts.pricing.type")}
                  </th>
                  <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>
                    {t("posProducts.pricing.value")}
                  </th>
                  <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>
                    {t("posProducts.pricing.appliesTo")}
                  </th>
                  <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>
                    {t("posProducts.pricing.period")}
                  </th>
                  <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>
                    {t("posProducts.status")}
                  </th>
                  <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>
                    {t("posProducts.actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {MOCK_PRICE_RULES.map((rule) => (
                  <tr key={rule.id} className={`border-b ${themeClasses.border} ${themeClasses.tableRowHover}`}>
                    <td className={`px-4 py-3 font-medium ${themeClasses.text}`}>{rule.name}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRuleTypeBadge(rule.type)}`}>
                        {rule.type}
                      </span>
                    </td>
                    <td className={`px-4 py-3 text-center font-medium ${themeClasses.text}`}>
                      {rule.type === "fixed" ? `$${rule.value}` : `${rule.value}%`}
                    </td>
                    <td className={`px-4 py-3 ${themeClasses.text}`}>
                      {rule.appliesTo === "all" ? t("posProducts.pricing.allProducts") : rule.targetName}
                    </td>
                    <td className={`px-4 py-3 text-center text-sm ${themeClasses.textSecondary}`}>
                      {rule.startDate && rule.endDate ? `${rule.startDate} - ${rule.endDate}` : "-"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          rule.isActive ? "bg-emerald-500/20 text-emerald-500" : "bg-gray-500/20 text-gray-500"
                        }`}
                      >
                        {rule.isActive ? t("posProducts.statusActive") : t("posProducts.statusInactive")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button aria-label={t("general.edit", { ns: "common" })} className={`p-2 rounded-lg ${themeClasses.tableRowHover} ${themeClasses.text}`}>
                          <i className="ri-edit-line"></i>
                        </button>
                        <button aria-label={t("general.delete", { ns: "common" })} className="p-2 rounded-lg hover:bg-red-500/10 text-red-500">
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${themeClasses.bg} rounded-2xl p-6 w-full max-w-md`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className={`text-xl font-bold ${themeClasses.text}`}>
                {activeTab === "lists" ? t("posProducts.pricing.addPriceList") : t("posProducts.pricing.addRule")}
              </h2>
              <button onClick={() => setShowCreateModal(false)} className={themeClasses.textSecondary}>
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
                  {t("posProducts.pricing.name")}
                </label>
                <input
                  type="text"
                  className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                />
              </div>
              {activeTab === "rules" && (
                <>
                  <div>
                    <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
                      {t("posProducts.pricing.type")}
                    </label>
                    <select
                      className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text}`}
                    >
                      <option value="discount">{t("posProducts.pricing.discount")}</option>
                      <option value="markup">{t("posProducts.pricing.markup")}</option>
                      <option value="fixed">{t("posProducts.pricing.fixedPrice")}</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${themeClasses.text} mb-2`}>
                      {t("posProducts.pricing.value")}
                    </label>
                    <input
                      type="number"
                      className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className={`flex-1 px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text}`}
              >
                {t("common.cancel")}
              </button>
              <button className="flex-1 px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium">
                {t("common.save")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
